import { LineSeparator } from '@/components/LineSeparator';
import { CreateCompanyInformations } from '@/partials/dashboard/Companies/CreateCompany/CreateCompanyInformations';
import { CreateCompanyLogo } from '@/partials/dashboard/Companies/CreateCompany/CreateCompanyLogo';
import { useCompanyStore } from '@/stores/useCompanyStore';
import i18n from '@/translations/config';
import { CreateCompanyForm } from '@/types/company';
import { handleError } from '@/utils/handleError';
import { Formik } from 'formik';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      duration: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 },
  },
};

export const CreateCompany = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createCompany } = useCompanyStore();

  const initialValues: CreateCompanyForm = {
    name: '',
    address: '',
    vat_number: '',
    phone: '',
    email: '',
    website: '',
    logo: null,
  };

  const validationSchema = yup.object().shape({
    name: yup.string().required(t('common:validation.name.required')),
    address: yup.string().nullable(),
    vat_number: yup.string().nullable(),
    phone: yup.string().nullable(),
    email: yup.string().email(t('common:validation.email.format')).nullable(),
    website: yup.string().nullable(),
    logo: yup.mixed().nullable(),
  });

  const handleCreateCompany = async (values: CreateCompanyForm) => {
    try {
      const { data } = await createCompany(values);
      navigate(`/${i18n.language}/dashboard/companies/${data.id}`);
    } catch (error) {
      handleError(error);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="h-full space-y-6 overflow-y-auto">
      {/* Header */}
      <motion.div
        className="flex flex-col gap-4 p-4"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header avec bouton retour */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100"
          >
            <FaArrowLeft className="size-4" />
            {t('common:back')}
          </button>
          <div className="h-6 w-px bg-gray-300" />
          <h1 className="text-lg font-semibold text-secondary">
            {t('createCompany:title')}
          </h1>
        </div>
      </motion.div>

      {/* Contenu */}
      <motion.div
        className="px-4 pb-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleCreateCompany}
        >
          {({ errors, touched, setFieldValue, values }) => (
            <div className="relative">
              <motion.div variants={itemVariants}>
                <CreateCompanyLogo
                  setFieldValue={setFieldValue}
                  values={values}
                />
              </motion.div>

              <motion.div variants={itemVariants}>
                <LineSeparator className="my-6" />
              </motion.div>

              <motion.div variants={itemVariants}>
                <CreateCompanyInformations errors={errors} touched={touched} />
              </motion.div>
            </div>
          )}
        </Formik>
      </motion.div>
    </div>
  );
};
